import { FiltersPanel, FilterField, Filter, Input, Icon, MultiSelect, Toggle } from '@chikara/ui';
import type { MultiSelectOption } from '@chikara/ui';
import type { CardFilters as CardFiltersState } from '@/features/card/types/card.types';
import { CARD_ALL_VALUE } from '@/features/card/constants/cardFilters.constants';
import { hasAnyCardFilter } from '@/features/card/utils/cardFilters.utils';
import { t } from '@/i18n';
import styles from './CardFilters.module.css';

export interface CardFilterOption {
  label: string;
  value: string;
}

export interface CardFiltersProps {
  /** Opciones del select de Estatus (catálogo). Se reciben por props. */
  statusOptions: CardFilterOption[];
  /** Opciones del select de Tipo (catálogo). Se reciben por props. */
  typeOptions: CardFilterOption[];
  /** Opciones del multiselect de Etiqueta (catálogo). Se reciben por props. */
  labelOptions: MultiSelectOption[];
  /** Borrador en edición (lo que el usuario ve en los controles). */
  draftFilters: CardFiltersState;
  /** Actualiza el borrador con un patch parcial. */
  onFilterChange: (patch: Partial<CardFiltersState>) => void;
  onApply: () => void;
  onClear: () => void;
  isApplying?: boolean;
  /** Si se provee, se muestra el control de descarga (seam BF-624). */
  onExportExcel?: () => void | Promise<void>;
  /** Descarga en PDF (seam BF-644/BF-643). */
  onExportPdf?: () => void | Promise<void>;
  /** Muestra la opción PDF deshabilitada mientras el BE no expone el endpoint. */
  pdfDisabled?: boolean;
  hasResults?: boolean;
  className?: string;
}

/**
 * Panel de filtros de la pestaña "Tarjetas asignadas" (HU3 / BF-625).
 *
 * Solo presentacional: recibe catálogos, borrador y callbacks por props; no
 * conoce el servicio ni el estado aplicado.
 */
export function CardFilters({
  statusOptions,
  typeOptions,
  labelOptions,
  draftFilters,
  onFilterChange,
  onApply,
  onClear,
  isApplying,
  onExportExcel,
  onExportPdf,
  pdfDisabled,
  hasResults,
  className,
}: CardFiltersProps) {
  const classes = [styles.wrapper, className].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      <FiltersPanel
        onApply={onApply}
        onClear={onClear}
        hasCriteria={hasAnyCardFilter(draftFilters)}
        isApplying={isApplying}
        onExportExcel={onExportExcel}
        onExportPdf={onExportPdf}
        pdfDisabled={pdfDisabled}
        hasResults={hasResults}
        applyLabel={t('dashboard.card.filters.apply')}
        clearLabel={t('dashboard.card.filters.clear')}
        exportLabel={t('dashboard.card.filters.download')}
        exportExcelLabel={t('dashboard.card.filters.downloadExcel')}
        exportPdfLabel={t('dashboard.card.filters.downloadPdf')}
      >
        <FilterField className={styles.field} label={t('dashboard.card.filters.emailLabel')} htmlFor="card-filter-search">
          <Input
            id="card-filter-search"
            type="search"
            size="sm"
            leadingIcon={<Icon name="search" size="sm" color="inherit" />}
            placeholder={t('dashboard.card.filters.emailPlaceholder')}
            value={draftFilters.search ?? ''}
            onChange={(event) => onFilterChange({ search: event.target.value })}
          />
        </FilterField>

        <FilterField className={styles.field} label={t('dashboard.card.filters.statusLabel')} htmlFor="card-filter-status">
          <Filter
            id="card-filter-status"
            size="sm"
            width="100%"
            value={draftFilters.status ?? CARD_ALL_VALUE}
            onChange={(value) => onFilterChange({ status: value === CARD_ALL_VALUE ? undefined : value })}
            options={[{ label: t('dashboard.card.filters.statusPlaceholder'), value: CARD_ALL_VALUE }, ...statusOptions]}
          />
        </FilterField>

        <FilterField className={styles.field} label={t('dashboard.card.filters.typeLabel')} htmlFor="card-filter-type">
          <Filter
            id="card-filter-type"
            size="sm"
            width="100%"
            value={draftFilters.type ?? CARD_ALL_VALUE}
            onChange={(value) => onFilterChange({ type: value === CARD_ALL_VALUE ? undefined : value })}
            options={[{ label: t('dashboard.card.filters.typePlaceholder'), value: CARD_ALL_VALUE }, ...typeOptions]}
          />
        </FilterField>

        <FilterField className={styles.field} label={t('dashboard.card.filters.labelLabel')} htmlFor="card-filter-label">
          <MultiSelect
            id="card-filter-label"
            size="sm"
            placeholder={t('dashboard.card.filters.labelPlaceholder')}
            options={labelOptions}
            value={draftFilters.tags ?? []}
            onChange={(tags) => onFilterChange({ tags: tags.length > 0 ? tags : undefined })}
            confirmLabel={t('dashboard.card.filters.confirm')}
          />
        </FilterField>

        <FilterField className={styles.field} label={t('dashboard.card.filters.noLimitLabel')} htmlFor="card-filter-no-limit">
          <Toggle
            id="card-filter-no-limit"
            checked={Boolean(draftFilters.noLimit)}
            onChange={(checked) => onFilterChange({ noLimit: checked || undefined })}
          />
        </FilterField>
      </FiltersPanel>
    </div>
  );
}

CardFilters.displayName = 'CardFilters';
